import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import AdminRouteGuard from '../../components/admin/AdminRouteGuard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminNoteThread from '../../components/admin/AdminNoteThread';
import { sb } from '../../supabase';
import { logAdminAction } from '../../lib/adminAudit';
import { sendMaabarEmail } from '../../lib/maabarEmail';
import { normalizeSupplierDocStoragePath } from '../../lib/supplierOnboarding';
import { getSpecialtyLabel } from '../../lib/supplierDashboardConstants';
import { PRODUCT_TIER_EMBED, deriveProductPriceFrom } from '../../lib/productPriceLookup';

const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY    = "'Tajawal', sans-serif";

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.80)', wordBreak: 'break-word', fontFamily: mono ? "'Courier New', monospace" : FONT_BODY }}>
        {value || '—'}
      </span>
    </div>
  );
}

function SectionCard({ title, children, style }) {
  return (
    <div style={{ background: 'var(--bg-raised, #fff)', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 10, padding: '20px 20px 18px', marginBottom: 12, ...style }}>
      {title && (
        <p style={{ margin: '0 0 16px', fontSize: 10, fontWeight: 600, letterSpacing: 1.6, textTransform: 'uppercase', color: 'rgba(0,0,0,0.38)', fontFamily: FONT_BODY }}>
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

function InfoGrid({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px 24px' }}>
      {children}
    </div>
  );
}

export default function AdminSupplierDetail({ user, profile, lang, ...rest }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [signedUrls, setSignedUrls] = useState({});

  // Phase 5 — product management
  const [products, setProducts] = useState([]);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [productVariants, setProductVariants] = useState({}); // productId → variants[]
  const [productTiers, setProductTiers] = useState({});       // productId → tiers[]
  const [productShipping, setProductShipping] = useState({}); // productId → shipping[]
  const [variantAction, setVariantAction] = useState(null);   // { variantId, type: 'toggle'|'stock', reason, value }
  const [savingVariant, setSavingVariant] = useState(null);

  const isAr = lang === 'ar';

  const getSignedUrl = useCallback(async (rawPath) => {
    if (!rawPath || typeof rawPath !== 'string') return null;
    // Tolerate mixed-format legacy entries in factory_images. Two upload
    // codepaths historically wrote to that column with different formats:
    //   - the verification flow stores supplier-docs paths (private,
    //     must be signed)
    //   - the older settings-tab uploadFactoryImage stored full public
    //     URLs to product-images (public bucket, no signing required)
    // Pre-fix, only the first form rendered; the second form fell out
    // of normalizeSupplierDocStoragePath() as '' and the admin saw
    // identical "…" placeholders in place of real images. Now: if the
    // raw value is already a fully-qualified URL to anything other
    // than supplier-docs, pass it through unchanged so the <img> can
    // load it directly. Path values and supplier-docs URLs continue
    // through the normal sign-and-return path below.
    if (/^https?:\/\//i.test(rawPath) && !rawPath.includes('/supplier-docs/')) {
      return rawPath;
    }
    const path = normalizeSupplierDocStoragePath(rawPath);
    if (!path) return null;
    const { data, error } = await sb.storage.from('supplier-docs').createSignedUrl(path, 60 * 30);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  }, []);


  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: supplierData }, { data: productsData }] = await Promise.all([
      sb.from('profiles').select('*').eq('id', id).single(),
      sb.from('products').select(`id, name_en, name_zh, name_ar, moq, is_active, has_variants, created_at, ${PRODUCT_TIER_EMBED}`).eq('supplier_id', id).order('created_at', { ascending: false }),
    ]);
    setSupplier(supplierData);
    // Fetch variant counts per product
    if (productsData?.length) {
      const variantCounts = await Promise.all(
        productsData.filter(p => p.has_variants).map(async (p) => {
          const { count } = await sb.from('product_variants').select('id', { count: 'exact', head: true }).eq('product_id', p.id);
          return [p.id, count || 0];
        })
      );
      const countMap = Object.fromEntries(variantCounts);
      setProducts((productsData || []).map(p => ({ ...p, variantCount: countMap[p.id] ?? null })));
    } else {
      setProducts(productsData || []);
    }
    setLoading(false);
  }, [id]);

  const loadProductVariants = useCallback(async (productId) => {
    const [varRes, tierRes, shipRes] = await Promise.all([
      sb.from('product_variants').select('*').eq('product_id', productId).order('created_at'),
      sb.from('product_pricing_tiers').select('*').eq('product_id', productId).order('qty_from'),
      sb.from('product_shipping_options').select('*').eq('product_id', productId),
    ]);
    setProductVariants(prev => ({ ...prev, [productId]: varRes.data || [] }));
    setProductTiers(prev => ({ ...prev, [productId]: tierRes.data || [] }));
    setProductShipping(prev => ({ ...prev, [productId]: shipRes.data || [] }));
  }, []);

  const toggleExpandProduct = (productId) => {
    if (expandedProductId === productId) { setExpandedProductId(null); return; }
    setExpandedProductId(productId);
    if (!productVariants[productId]) loadProductVariants(productId);
  };

  const adminToggleVariant = async (variant, reason) => {
    if (!reason?.trim()) { alert('Please enter a reason for the audit log.'); return; }
    setSavingVariant(variant.id);
    const newActive = !variant.is_active;
    const { error } = await sb.from('product_variants').update({ is_active: newActive }).eq('id', variant.id);
    if (error) { alert('Error: ' + error.message); setSavingVariant(null); return; }
    await logAdminAction({ actorId: user.id, action: newActive ? 'variant_activated' : 'variant_deactivated', entityType: 'product_variant', entityId: variant.id, beforeState: { is_active: variant.is_active }, afterState: { is_active: newActive }, notes: reason });
    await loadProductVariants(variant.product_id);
    setVariantAction(null);
    setSavingVariant(null);
    flash(`Variant ${newActive ? 'activated' : 'deactivated'}`);
  };

  const adminAdjustStock = async (variant, newStock, reason) => {
    if (!reason?.trim()) { alert('Please enter a reason for the audit log.'); return; }
    const stockNum = parseInt(newStock, 10);
    if (isNaN(stockNum) || stockNum < 0) { alert('Invalid stock value.'); return; }
    setSavingVariant(variant.id);
    const { error } = await sb.from('product_variants').update({ stock: stockNum }).eq('id', variant.id);
    if (error) { alert('Error: ' + error.message); setSavingVariant(null); return; }
    await logAdminAction({ actorId: user.id, action: 'variant_stock_adjusted', entityType: 'product_variant', entityId: variant.id, beforeState: { stock: variant.stock }, afterState: { stock: stockNum }, notes: reason });
    await loadProductVariants(variant.product_id);
    setVariantAction(null);
    setSavingVariant(null);
    flash(`Stock updated to ${stockNum}`);
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!supplier) return;
    const rawPaths = [
      supplier.license_photo,
      supplier.legal_rep_id_photo,
      supplier.address_proof_photo,
      supplier.factory_photo,
      ...(Array.isArray(supplier.factory_images) ? supplier.factory_images : []),
      ...(Array.isArray(supplier.factory_videos) ? supplier.factory_videos : []),
    ].filter(Boolean);

    Promise.all(
      rawPaths.map(async (raw) => {
        const signed = await getSignedUrl(raw);
        return [raw, signed];
      })
    ).then((entries) => {
      setSignedUrls(Object.fromEntries(entries.filter(([, v]) => v)));
    });
  }, [supplier, getSignedUrl]);

  // In-page attachment viewer. Reviewing a supplier used to mean leaving the page
  // for every single file (window.open / target="_blank"), losing your place and
  // your scroll position each time. Everything is signed up-front anyway, so it
  // can all be inspected here without navigating away.
  const [viewerIdx, setViewerIdx] = useState(null);
  const viewerOpen = viewerIdx !== null;

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setViewerIdx(null);
      if (e.key === 'ArrowRight') setViewerIdx(i => (i === null ? i : i + 1));
      if (e.key === 'ArrowLeft')  setViewerIdx(i => (i === null ? i : i - 1));
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [viewerOpen]);

  // ── Share links ──────────────────────────────────────────────────────────
  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const [shareLinks, setShareLinks] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');

  const loadShareLinks = useCallback(async () => {
    const { data } = await sb.from('supplier_share_links')
      .select('token, created_at, expires_at, revoked_at, view_count, last_viewed_at')
      .eq('supplier_id', id)
      .order('created_at', { ascending: false });
    setShareLinks(data || []);
  }, [id]);

  useEffect(() => { loadShareLinks(); }, [loadShareLinks]);

  const shareUrlFor = (token) => `${window.location.origin}/s/supplier/${token}`;

  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
    } catch { /* fall through */ }
    // Non-secure contexts (e.g. testing over a LAN IP) have no clipboard API.
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  const createShareLink = async () => {
    setSharing(true);
    const { data: token, error } = await sb.rpc('create_supplier_share_link', { p_supplier_id: id, p_days: 30 });
    if (error || !token) {
      flash((isAr ? 'تعذّر إنشاء الرابط: ' : 'Could not create link: ') + (error?.message || ''));
      setSharing(false); return;
    }
    const url = shareUrlFor(token);
    const copied = await copyText(url);
    setCopiedToken(copied ? token : '');
    await logAdminAction({ actorId: user.id, action: 'supplier_share_link_create', entityType: 'supplier', entityId: id, beforeState: null, afterState: { expires_days: 30 } });
    await loadShareLinks();
    setSharing(false);
    flash(copied
      ? (isAr ? 'تم إنشاء الرابط ونسخه' : 'Link created and copied')
      : (isAr ? 'تم إنشاء الرابط — انسخه يدوياً' : 'Link created — copy it manually'));
  };

  const revokeShareLink = async (token) => {
    const { error } = await sb.rpc('revoke_supplier_share_link', { p_token: token });
    if (error) { flash((isAr ? 'تعذّر الإلغاء: ' : 'Could not revoke: ') + error.message); return; }
    await logAdminAction({ actorId: user.id, action: 'supplier_share_link_revoke', entityType: 'supplier', entityId: id, beforeState: { token: token.slice(0, 8) }, afterState: { revoked: true } });
    await loadShareLinks();
    flash(isAr ? 'تم إلغاء الرابط' : 'Link revoked');
  };

  const flash = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 3500);
  };

  const updateStatus = async (newStatus, reason) => {
    if (!supplier) return;
    setSaving(true);
    const before = { status: supplier.status };
    const { error } = await sb.from('profiles').update({ status: newStatus }).eq('id', id);
    if (error) {
      flash(isAr ? `خطأ: ${error.message}` : `Error: ${error.message}`);
      setSaving(false);
      return;
    }
    await logAdminAction({
      actorId: user.id,
      action: `supplier_${newStatus}`,
      entityType: 'supplier',
      entityId: id,
      beforeState: before,
      afterState: { status: newStatus },
      notes: reason || null,
    });
    if (newStatus === 'verified') {
      try {
        await sendMaabarEmail({
          type: 'supplier_approved',
          to: supplier.email,
          data: { name: supplier.full_name || '', lang: supplier.lang || 'ar' },
        });
      } catch (e) { console.error('approval email error:', e); }
    }
    await load();
    flash(isAr ? 'تم التحديث بنجاح' : 'Status updated');
    setSaving(false);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const CSS = `
    .sd-page { padding: 32px 32px; max-width: 920px; }
    .sd-back { display: inline-flex; align-items: center; gap: 6px; background: none; border: none; cursor: pointer; color: rgba(0,0,0,0.38); font-size: 12px; padding: 0 0 22px; font-family: ${FONT_BODY}; min-height: 44px; letter-spacing: 0.3px; transition: color 0.12s; }
    .sd-back:hover { color: rgba(0,0,0,0.65); }
    .sd-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
    .sd-action-bar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .sd-btn { min-height: 40px; padding: 0 18px; border-radius: 8px; border: none; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: ${FONT_BODY}; white-space: nowrap; }
    .sd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .sd-btn-approve { background: #27725a; color: #fff; }
    .sd-btn-approve:hover:not(:disabled) { background: #1f5f49; }
    .sd-btn-reject { background: rgba(192,57,43,0.08); color: #c0392b; border: 1px solid rgba(192,57,43,0.2); }
    .sd-btn-reject:hover:not(:disabled) { background: rgba(192,57,43,0.13); }
    .sd-btn-ghost { background: transparent; color: rgba(0,0,0,0.45); border: 1px solid rgba(0,0,0,0.09); }
    .sd-btn-ghost:hover:not(:disabled) { background: rgba(0,0,0,0.04); color: rgba(0,0,0,0.72); }
    .sd-flash { position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%); background: #1a1814; color: #fff; padding: 10px 22px; border-radius: 99px; font-size: 12px; font-family: ${FONT_BODY}; z-index: 999; white-space: nowrap; pointer-events: none; }
    .sd-doc-link { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid rgba(0,0,0,0.09); border-radius: 8px; background: transparent; color: rgba(0,0,0,0.65); font-size: 12px; cursor: pointer; font-family: ${FONT_BODY}; text-decoration: none; min-height: 40px; transition: all 0.12s; }
    .sd-doc-link:hover { background: rgba(0,0,0,0.04); border-color: rgba(0,0,0,0.14); }
    .sd-img-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 8px; }
    .sd-img { width: 100%; aspect-ratio: 4/3; object-fit: cover; border-radius: 8px; background: var(--bg-subtle, #F5F2EE); border: 1px solid rgba(0,0,0,0.07); cursor: pointer; display: block; }
    @media (max-width: 900px) { .sd-page { padding: 22px 16px; } }
    @media (max-width: 600px) { .sd-header { flex-direction: column; } .sd-action-bar { width: 100%; } .sd-btn { flex: 1; text-align: center; } }
  `;

  if (loading) return (
    <AdminRouteGuard user={user} profile={profile} lang={lang}>
      <AdminShell user={user} profile={profile} lang={lang}>
        <style>{CSS}</style>
        <div className="sd-page"><p style={{ color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY, fontSize: 13 }}>Loading…</p></div>
      </AdminShell>
    </AdminRouteGuard>
  );

  if (!supplier) return (
    <AdminRouteGuard user={user} profile={profile} lang={lang}>
      <AdminShell user={user} profile={profile} lang={lang}>
        <style>{CSS}</style>
        <div className="sd-page"><p style={{ color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY, fontSize: 13 }}>Supplier not found.</p></div>
      </AdminShell>
    </AdminRouteGuard>
  );

  // Dedupe: factory_photo is mirrored from factory_images[0] on every
  // supplier-side upload, so a naive [factory_photo, ...factory_images]
  // produced a duplicate of the first image. Wrap in a Set to keep the
  // legacy fallback (when factory_images is empty but factory_photo is
  // set) without rendering the same path twice.
  const images = Array.from(new Set(
    [supplier.factory_photo, ...(Array.isArray(supplier.factory_images) ? supplier.factory_images : [])].filter(Boolean)
  ));
  const videos = Array.isArray(supplier.factory_videos) ? supplier.factory_videos.filter(Boolean) : [];
  const docs = [
    supplier.license_photo && {
      label: isAr ? 'السجل التجاري' : lang === 'zh' ? '营业执照' : 'Business License',
      url: supplier.license_photo,
    },
    supplier.legal_rep_id_photo && {
      label: isAr ? 'هوية الممثل القانوني' : lang === 'zh' ? '法定代表人身份证' : 'Legal Representative ID',
      url: supplier.legal_rep_id_photo,
    },
    supplier.address_proof_photo && {
      label: isAr ? 'إثبات عنوان المصنع' : lang === 'zh' ? '工厂地址证明' : 'Factory/Office Address Proof',
      url: supplier.address_proof_photo,
    },
  ].filter(Boolean);

  // One flat list so the viewer can page through every attachment in order.
  const kindOf = (path = '') => {
    const p = String(path).toLowerCase().split('?')[0];
    if (/\.(mp4|mov|webm|m4v|avi|mkv)$/.test(p)) return 'video';
    if (/\.pdf$/.test(p)) return 'pdf';
    return 'image';
  };
  const attachments = [
    ...docs.map(d => ({ raw: d.url, label: d.label, kind: kindOf(d.url) })),
    ...images.map((raw, i) => ({ raw, label: `${isAr ? 'صورة المصنع' : 'Factory photo'} ${i + 1}`, kind: 'image' })),
    ...videos.map((raw, i) => ({ raw, label: `${isAr ? 'فيديو المصنع' : 'Factory video'} ${i + 1}`, kind: 'video' })),
  ].map(a => ({ ...a, url: signedUrls[a.raw] || null }));

  const idxOf = (raw) => attachments.findIndex(a => a.raw === raw);
  const openViewer = (raw) => { const i = idxOf(raw); if (i >= 0) setViewerIdx(i); };
  const current = viewerOpen ? attachments[Math.max(0, Math.min(viewerIdx, attachments.length - 1))] : null;

  const Thumb = ({ att }) => (
    <button
      onClick={() => openViewer(att.raw)}
      title={att.label}
      style={{
        position: 'relative', padding: 0, cursor: 'pointer', overflow: 'hidden',
        width: 132, height: 108, borderRadius: 10, background: 'var(--bg-subtle, #F5F2EE)',
        border: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center',
      }}
    >
      {att.url && att.kind === 'image' ? (
        <img src={att.url} alt={att.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : att.url && att.kind === 'video' ? (
        <>
          <video src={att.url} preload="metadata" muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <span style={{ position: 'relative', width: 30, height: 30, borderRadius: '50%', background: 'rgba(26,24,20,0.72)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>▶</span>
        </>
      ) : att.url && att.kind === 'pdf' ? (
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'rgba(0,0,0,0.45)', fontFamily: FONT_BODY }}>PDF</span>
      ) : (
        <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.30)', fontFamily: FONT_BODY }}>…</span>
      )}
      <span style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, padding: '5px 7px',
        background: 'linear-gradient(transparent, rgba(0,0,0,0.62))', color: '#fff',
        fontSize: 9.5, fontFamily: FONT_BODY, lineHeight: 1.25,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {att.label}
      </span>
    </button>
  );

  return (
    <AdminRouteGuard user={user} profile={profile} lang={lang}>
      <AdminShell user={user} profile={profile} lang={lang}>
        <style>{CSS}</style>
        {actionMsg && <div className="sd-flash">{actionMsg}</div>}

        <div className="sd-page" dir={isAr ? 'rtl' : 'ltr'}>
          <button className="sd-back" onClick={() => nav('/admin/suppliers')}>
            {isAr ? '‹ الموردون' : '‹ Suppliers'}
          </button>

          {/* Header */}
          <div className="sd-header">
            <div>
              <h1 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 400, color: 'rgba(0,0,0,0.88)', fontFamily: FONT_HEADING, lineHeight: 1.1 }}>
                {supplier.full_name || supplier.email}
              </h1>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <AdminStatusBadge status={supplier.status} lang={lang} />
                {supplier.maabar_supplier_id && (
                  <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.30)', fontFamily: FONT_BODY, letterSpacing: 0.4 }}>{supplier.maabar_supplier_id}</span>
                )}
              </div>
            </div>
            <div className="sd-action-bar">
              {supplier.status === 'verification_under_review' && (
                <>
                  <button className="sd-btn sd-btn-approve" disabled={saving} onClick={() => updateStatus('verified')}>
                    {saving ? '…' : (isAr ? 'قبول المورد' : 'Approve')}
                  </button>
                  <button className="sd-btn sd-btn-reject" disabled={saving} onClick={() => setShowRejectForm(v => !v)}>
                    {isAr ? 'رفض' : 'Reject'}
                  </button>
                </>
              )}
              {supplier.status === 'verified' && (
                <button className="sd-btn sd-btn-ghost" disabled={saving} onClick={() => updateStatus('inactive')}>
                  {isAr ? 'إيقاف' : 'Deactivate'}
                </button>
              )}
              {(supplier.status === 'inactive' || supplier.status === 'rejected') && (
                <button className="sd-btn sd-btn-approve" disabled={saving} onClick={() => updateStatus('verified')}>
                  {isAr ? 'إعادة تفعيل' : 'Reactivate'}
                </button>
              )}
            </div>
          </div>

          {/* Reject form */}
          {showRejectForm && (
            <SectionCard style={{ marginBottom: 12, borderColor: 'rgba(192,57,43,0.20)' }}>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: 'rgba(0,0,0,0.55)', fontFamily: FONT_BODY }}>
                {isAr ? 'سبب الرفض (اختياري، للسجل الداخلي)' : 'Rejection reason (optional, internal record)'}
              </p>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder={isAr ? 'سبب الرفض...' : 'Reason for rejection…'}
                dir={isAr ? 'rtl' : 'ltr'}
                style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-subtle, #F5F2EE)', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: 'rgba(0,0,0,0.80)', fontFamily: FONT_BODY, resize: 'vertical', outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="sd-btn sd-btn-reject" disabled={saving} onClick={() => updateStatus('rejected', rejectReason)}>
                  {saving ? '…' : (isAr ? 'تأكيد الرفض' : 'Confirm Rejection')}
                </button>
                <button className="sd-btn sd-btn-ghost" onClick={() => setShowRejectForm(false)}>{isAr ? 'إلغاء' : 'Cancel'}</button>
              </div>
            </SectionCard>
          )}

          {/* Account info */}
          <SectionCard title={isAr ? 'معلومات الحساب' : 'Account Info'}>
            <InfoGrid>
              <InfoRow label={isAr ? 'الاسم الكامل' : 'Full Name'} value={supplier.full_name} />
              <InfoRow label="Email" value={supplier.email} mono />
              <InfoRow label={isAr ? 'واتساب' : 'WhatsApp'} value={supplier.whatsapp} mono />
              <InfoRow label="WeChat" value={supplier.wechat} mono />
              <InfoRow label={isAr ? 'تاريخ الانضمام' : 'Joined'} value={supplier.created_at ? new Date(supplier.created_at).toLocaleString('en-GB') : '—'} mono />
              <InfoRow label={isAr ? 'معرّف معبر' : 'Maabar ID'} value={supplier.maabar_supplier_id} mono />
            </InfoGrid>
          </SectionCard>

          {/* Company */}
          <SectionCard title={isAr ? 'بيانات الشركة' : 'Company'}>
            <InfoGrid>
              <InfoRow label={isAr ? 'اسم الشركة' : 'Company Name'} value={supplier.company_name} />
              <InfoRow label={isAr ? 'الدولة' : 'Country'} value={supplier.country} />
              <InfoRow label={isAr ? 'المدينة' : 'City'} value={supplier.city} />
              <InfoRow label={isAr ? 'التخصص' : 'Speciality'} value={getSpecialtyLabel(supplier.speciality, lang)} />
              <InfoRow label={isAr ? 'نوع النشاط' : 'Business Type'} value={supplier.business_type} />
              <InfoRow label={isAr ? 'سنة التأسيس' : 'Est. Year'} value={supplier.year_established} />
              <InfoRow label={isAr ? 'عدد الموظفين' : 'Employees'} value={supplier.num_employees} />
              <InfoRow label={isAr ? 'سنوات الخبرة' : 'Experience (yrs)'} value={supplier.years_experience} />
              <InfoRow label={isAr ? 'رقم السجل' : 'Reg Number'} value={supplier.reg_number} mono />
            </InfoGrid>
            {supplier.trade_link && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 4px', fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>
                  {isAr ? 'رابط التجارة' : 'Trade Link'}
                </p>
                <a href={supplier.trade_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'rgba(0,0,0,0.60)', wordBreak: 'break-all', fontFamily: FONT_BODY }}>
                  {supplier.trade_link} ↗
                </a>
              </div>
            )}
          </SectionCard>

          {/* Share with an outside reviewer (e.g. a partner verifying the factory
              on the ground in China). The link carries identity documents, so it
              expires, is revocable, and every view is counted. */}
          <SectionCard title={isAr ? 'مشاركة الملف' : 'Share Dossier'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: shareLinks.length ? 16 : 0 }}>
              <button className="sd-btn sd-btn-approve" onClick={createShareLink} disabled={sharing}>
                {sharing ? '…' : (isAr ? 'إنشاء رابط مشاركة' : 'Create share link')}
              </button>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.42)', fontFamily: FONT_BODY, lineHeight: 1.6 }}>
                {isAr
                  ? 'صالح ٣٠ يوماً · يفتحه أي شخص بلا حساب · يعرض الوثائق كاملة'
                  : 'Valid 30 days · opens without an account · shows full documents'}
              </span>
            </div>

            {shareLinks.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {shareLinks.map(l => {
                  const expired = new Date(l.expires_at).getTime() < Date.now();
                  const dead = !!l.revoked_at || expired;
                  return (
                    <div key={l.token} style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      padding: '10px 12px', borderRadius: 8,
                      background: dead ? 'rgba(0,0,0,0.02)' : 'var(--bg-subtle, #F5F2EE)',
                      border: '1px solid rgba(0,0,0,0.06)', opacity: dead ? 0.6 : 1,
                    }}>
                      <code style={{ fontSize: 11, color: 'rgba(0,0,0,0.55)', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1, minWidth: 140 }}>
                        …/s/supplier/{l.token.slice(0, 10)}…
                      </code>
                      <span style={{ fontSize: 10.5, color: 'rgba(0,0,0,0.42)', fontFamily: FONT_BODY, whiteSpace: 'nowrap' }}>
                        {l.revoked_at
                          ? (isAr ? 'مُلغى' : 'Revoked')
                          : expired
                            ? (isAr ? 'منتهٍ' : 'Expired')
                            : `${isAr ? 'ينتهي' : 'expires'} ${fmtDate(l.expires_at)}`}
                        {' · '}
                        {l.view_count || 0} {isAr ? 'مشاهدة' : 'views'}
                      </span>
                      {!dead && (
                        <>
                          <button className="sd-btn" onClick={async () => {
                            const ok = await copyText(shareUrlFor(l.token));
                            setCopiedToken(ok ? l.token : '');
                            flash(ok ? (isAr ? 'تم النسخ' : 'Copied') : (isAr ? 'تعذّر النسخ' : 'Copy failed'));
                          }}>
                            {copiedToken === l.token ? (isAr ? 'نُسخ ✓' : 'Copied ✓') : (isAr ? 'نسخ' : 'Copy')}
                          </button>
                          <button className="sd-btn sd-btn-reject" onClick={() => revokeShareLink(l.token)}>
                            {isAr ? 'إلغاء' : 'Revoke'}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          {/* All attachments, inline. Everything the reviewer needs is visible at a
              glance here and opens in-page — no tab-hopping mid-review. */}
          {attachments.length > 0 && (
            <SectionCard title={`${isAr ? 'المرفقات' : 'Attachments'} (${attachments.length})`}>
              {docs.length > 0 && (
                <>
                  <p style={{ margin: '0 0 8px', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>
                    {isAr ? 'المستندات الرسمية' : 'Official documents'}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                    {attachments.slice(0, docs.length).map((att, i) => <Thumb key={`d${i}`} att={att} />)}
                  </div>
                </>
              )}
              {images.length > 0 && (
                <>
                  <p style={{ margin: '0 0 8px', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>
                    {isAr ? 'صور المصنع' : 'Factory photos'}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                    {attachments.slice(docs.length, docs.length + images.length).map((att, i) => <Thumb key={`i${i}`} att={att} />)}
                  </div>
                </>
              )}
              {videos.length > 0 && (
                <>
                  <p style={{ margin: '0 0 8px', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>
                    {isAr ? 'فيديو المصنع' : 'Factory video'}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {attachments.slice(docs.length + images.length).map((att, i) => <Thumb key={`v${i}`} att={att} />)}
                  </div>
                </>
              )}
            </SectionCard>
          )}

          {/* ── Phase 5: Products ── */}
          {products.length > 0 && (
            <SectionCard title="Products">
              {products.map(p => {
                const isExpanded = expandedProductId === p.id;
                const variants = productVariants[p.id] || [];
                const tiers = productTiers[p.id] || [];
                const shipping = productShipping[p.id] || [];
                const productName = p.name_en || p.name_zh || p.name_ar || p.id;
                return (
                  <div key={p.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', marginBottom: 0, paddingBottom: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', cursor: 'pointer' }} onClick={() => p.has_variants && toggleExpandProduct(p.id)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.80)', marginBottom: 2, fontFamily: FONT_BODY }}>{productName}</p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>MOQ {p.moq || '—'}</span>
                          {(() => { const pf = deriveProductPriceFrom(p); return pf ? <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>${pf}</span> : null; })()}
                          {p.has_variants && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'rgba(0,0,0,0.05)', color: 'rgba(0,0,0,0.40)', fontFamily: FONT_BODY }}>{p.variantCount ?? '?'} variants</span>}
                          {/* Flag unusual pricing: product > 10x no baseline, just flag if no price */}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: p.is_active ? 'rgba(39,114,90,0.1)' : 'rgba(0,0,0,0.05)', color: p.is_active ? '#27725a' : 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY }}>{p.is_active ? 'Active' : 'Inactive'}</span>
                      {p.has_variants && <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.30)', fontFamily: FONT_BODY }}>{isExpanded ? '▲' : '▼'}</span>}
                    </div>

                    {isExpanded && p.has_variants && (
                      <div style={{ paddingBottom: 16 }}>
                        {/* Variant matrix */}
                        {variants.length > 0 ? (
                          <div style={{ overflowX: 'auto', marginBottom: 14 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: FONT_BODY }}>
                              <thead>
                                <tr style={{ background: 'rgba(0,0,0,0.03)' }}>
                                  {['SKU', 'Price (USD)', 'MOQ', 'Stock', 'Lead (days)', 'Active', 'Actions'].map(h => (
                                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'rgba(0,0,0,0.38)', fontWeight: 600, letterSpacing: 0.8, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {variants.map(v => {
                                  const basePrice = deriveProductPriceFrom(products.find(pr => pr.id === v.product_id));
                                  const unusualPrice = basePrice && v.price && v.price > basePrice * 10;
                                  return (
                                    <tr key={v.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: 'rgba(0,0,0,0.60)', fontSize: 10 }}>{v.sku || '—'}</td>
                                      <td style={{ padding: '8px 10px', color: unusualPrice ? '#c0392b' : 'rgba(0,0,0,0.70)' }}>
                                        ${Number(v.price || 0).toFixed(2)}
                                        {unusualPrice && <span style={{ marginInlineStart: 4, fontSize: 9, background: 'rgba(192,57,43,0.1)', color: '#c0392b', padding: '1px 5px', borderRadius: 99 }}>⚠ unusual</span>}
                                      </td>
                                      <td style={{ padding: '8px 10px', color: 'rgba(0,0,0,0.60)' }}>{v.moq || '—'}</td>
                                      <td style={{ padding: '8px 10px', color: v.stock != null ? (v.stock > 0 ? '#27725a' : '#c0392b') : 'rgba(0,0,0,0.30)' }}>{v.stock ?? '—'}</td>
                                      <td style={{ padding: '8px 10px', color: 'rgba(0,0,0,0.60)' }}>{v.lead_time_days ?? '—'}</td>
                                      <td style={{ padding: '8px 10px' }}>
                                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: v.is_active ? 'rgba(39,114,90,0.1)' : 'rgba(0,0,0,0.05)', color: v.is_active ? '#27725a' : 'rgba(0,0,0,0.35)' }}>
                                          {v.is_active ? 'Active' : 'Off'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                                        <button
                                          onClick={() => setVariantAction({ variantId: v.id, productId: v.product_id, type: 'toggle', variant: v, reason: '', value: '' })}
                                          style={{ fontSize: 10, padding: '3px 8px', marginInlineEnd: 6, background: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, cursor: 'pointer', color: 'rgba(0,0,0,0.50)', fontFamily: FONT_BODY }}>
                                          {v.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button
                                          onClick={() => setVariantAction({ variantId: v.id, productId: v.product_id, type: 'stock', variant: v, reason: '', value: String(v.stock ?? '') })}
                                          style={{ fontSize: 10, padding: '3px 8px', background: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, cursor: 'pointer', color: 'rgba(0,0,0,0.50)', fontFamily: FONT_BODY }}>
                                          Stock
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p style={{ fontSize: 12, color: 'rgba(0,0,0,0.35)', fontFamily: FONT_BODY, padding: '8px 0' }}>No active variants found.</p>
                        )}

                        {/* Inline action form */}
                        {variantAction?.productId === p.id && (
                          <div style={{ padding: '14px 16px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(0,0,0,0.02)', marginBottom: 12 }}>
                            <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.60)', fontFamily: FONT_BODY, marginBottom: 10 }}>
                              {variantAction.type === 'toggle' ? `${variantAction.variant.is_active ? 'Deactivate' : 'Activate'} variant — ${variantAction.variant.sku || variantAction.variantId.slice(0, 8)}` : `Adjust stock — ${variantAction.variant.sku || variantAction.variantId.slice(0, 8)}`}
                            </p>
                            {variantAction.type === 'stock' && (
                              <div style={{ marginBottom: 8 }}>
                                <label style={{ fontSize: 10, color: 'rgba(0,0,0,0.40)', letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: FONT_BODY, display: 'block', marginBottom: 4 }}>New Stock Qty</label>
                                <input
                                  type="number" min="0"
                                  value={variantAction.value}
                                  onChange={e => setVariantAction(prev => ({ ...prev, value: e.target.value }))}
                                  style={{ width: 120, padding: '6px 10px', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, fontSize: 13, fontFamily: FONT_BODY }}
                                />
                              </div>
                            )}
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 10, color: 'rgba(0,0,0,0.40)', letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: FONT_BODY, display: 'block', marginBottom: 4 }}>Reason (required, logged to audit_log)</label>
                              <input
                                type="text"
                                value={variantAction.reason}
                                onChange={e => setVariantAction(prev => ({ ...prev, reason: e.target.value }))}
                                placeholder="e.g. Quality issue, restock, admin correction…"
                                style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, fontSize: 13, fontFamily: FONT_BODY }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="sd-btn sd-btn-approve"
                                disabled={savingVariant === variantAction.variantId}
                                onClick={() => {
                                  if (variantAction.type === 'toggle') adminToggleVariant(variantAction.variant, variantAction.reason);
                                  else adminAdjustStock(variantAction.variant, variantAction.value, variantAction.reason);
                                }}
                              >
                                {savingVariant === variantAction.variantId ? '…' : 'Confirm'}
                              </button>
                              <button className="sd-btn sd-btn-ghost" onClick={() => setVariantAction(null)}>Cancel</button>
                            </div>
                          </div>
                        )}

                        {/* Tiered pricing (read-only) */}
                        {tiers.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <p style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', marginBottom: 6, fontFamily: FONT_BODY }}>Tiered Pricing</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {tiers.map((t, i) => (
                                <div key={i} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
                                  <p style={{ fontSize: 10, color: 'rgba(0,0,0,0.40)', fontFamily: FONT_BODY }}>{t.qty_to ? `${t.qty_from}–${t.qty_to}` : `${t.qty_from}+`}</p>
                                  <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(0,0,0,0.70)', fontFamily: FONT_BODY }}>${Number(t.unit_price).toFixed(2)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Shipping options (read-only) */}
                        {shipping.length > 0 && (
                          <div>
                            <p style={{ fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', marginBottom: 6, fontFamily: FONT_BODY }}>Shipping Options</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {shipping.map((s, i) => (
                                <div key={i} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)', background: s.is_available ? 'rgba(39,114,90,0.05)' : 'rgba(0,0,0,0.02)' }}>
                                  <p style={{ fontSize: 10, color: 'rgba(0,0,0,0.40)', fontFamily: FONT_BODY, textTransform: 'capitalize' }}>{s.method}</p>
                                  <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.60)', fontFamily: FONT_BODY }}>
                                    {(s.lead_time_min_days != null || s.lead_time_max_days != null)
                                      ? (s.lead_time_min_days != null && s.lead_time_max_days != null && s.lead_time_min_days !== s.lead_time_max_days
                                          ? `${s.lead_time_min_days}–${s.lead_time_max_days}d`
                                          : `${s.lead_time_min_days ?? s.lead_time_max_days}d`)
                                      : '—'}
                                    {s.cost_per_unit_usd != null ? ` · $${Number(s.cost_per_unit_usd).toFixed(2)}` : ''}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </SectionCard>
          )}

          {/* Notes */}
          <SectionCard>
            <AdminNoteThread entityType="supplier" entityId={id} user={user} lang={lang} />
          </SectionCard>
        </div>

        {/* ── In-page attachment viewer ── */}
        {viewerOpen && current && (
          <div
            onClick={() => setViewerIdx(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(20,18,15,0.92)',
              backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Bar */}
            <div
              onClick={e => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '14px 18px', color: 'rgba(255,255,255,0.92)', flexShrink: 0,
                borderBottom: '1px solid rgba(255,255,255,0.10)',
              }}
              dir={isAr ? 'rtl' : 'ltr'}
            >
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, fontFamily: FONT_BODY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {current.label}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: FONT_BODY, fontVariantNumeric: 'lining-nums' }}>
                  {viewerIdx + 1} / {attachments.length}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                {current.url && (
                  <a
                    href={current.url} target="_blank" rel="noopener noreferrer" download
                    style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, padding: '7px 12px', fontFamily: FONT_BODY, minHeight: 36, display: 'flex', alignItems: 'center' }}
                  >
                    {isAr ? 'تنزيل' : 'Download'}
                  </a>
                )}
                <button
                  onClick={() => setViewerIdx(null)}
                  style={{ fontSize: 18, lineHeight: 1, color: 'rgba(255,255,255,0.8)', background: 'transparent', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, cursor: 'pointer', width: 36, height: 36 }}
                  aria-label={isAr ? 'إغلاق' : 'Close'}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Stage */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 14, minHeight: 0 }}>
              <button
                onClick={e => { e.stopPropagation(); setViewerIdx(i => (i - 1 + attachments.length) % attachments.length); }}
                disabled={attachments.length < 2}
                style={{ flexShrink: 0, width: 42, height: 42, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 18, cursor: attachments.length < 2 ? 'default' : 'pointer', opacity: attachments.length < 2 ? 0.3 : 1 }}
                aria-label="Previous"
              >
                ‹
              </button>

              <div onClick={e => e.stopPropagation()} style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                {!current.url ? (
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontFamily: FONT_BODY }}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</p>
                ) : current.kind === 'image' ? (
                  <img src={current.url} alt={current.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
                ) : current.kind === 'video' ? (
                  <video src={current.url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, background: '#000' }} />
                ) : (
                  <iframe src={current.url} title={current.label} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, background: '#fff' }} />
                )}
              </div>

              <button
                onClick={e => { e.stopPropagation(); setViewerIdx(i => (i + 1) % attachments.length); }}
                disabled={attachments.length < 2}
                style={{ flexShrink: 0, width: 42, height: 42, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 18, cursor: attachments.length < 2 ? 'default' : 'pointer', opacity: attachments.length < 2 ? 0.3 : 1 }}
                aria-label="Next"
              >
                ›
              </button>
            </div>

            {/* Filmstrip */}
            {attachments.length > 1 && (
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 7, padding: '10px 14px', overflowX: 'auto', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.10)' }}>
                {attachments.map((a, i) => (
                  <button
                    key={i}
                    onClick={() => setViewerIdx(i)}
                    title={a.label}
                    style={{
                      flexShrink: 0, width: 56, height: 42, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', padding: 0,
                      border: i === viewerIdx ? '2px solid #fff' : '1px solid rgba(255,255,255,0.20)',
                      opacity: i === viewerIdx ? 1 : 0.55, background: 'rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {a.url && a.kind === 'image'
                      ? <img src={a.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.8)', fontFamily: FONT_BODY }}>{a.kind === 'video' ? '▶' : 'PDF'}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </AdminShell>
    </AdminRouteGuard>
  );
}
